/**
 * CollapsibleSection
 *
 * Shared row-clickable collapsible section primitive: 100%-width header
 * (label + chevron + optional secondary HTML + optional action HTML,
 * right-aligned) with a body that shows/hides on click. Caller owns its own
 * expand-state map.
 *
 * Used by FocusAreasSection's detail panel (historical) and the sidebar's
 * Recents section (SidebarRecentsController). Designed to
 * be reused by Discovery / Sessions / Practices detail pages later.
 *
 * @module components/CollapsibleSection
 */

import { escapeHtml } from '../core/utils.js';

/**
 * Render a row-clickable collapsible section header + body as an HTML string.
 *
 * WARNING: secondaryHtml, actionHtml, and bodyHtml are inserted as raw HTML.
 * Callers MUST escape any user-controlled text before passing.
 *
 * KNOWN LIMITATION: the action slot renders as a descendant of the toggle
 * <button>, which is technically invalid HTML if the action is itself
 * interactive (the current .fa-section__action is an <a>). This is preserved
 * from the pre-existing pattern and works reliably in all browsers via the
 * JS opt-out below; a future pass may refactor to sibling-markup.
 *
 * @param {Object}  opts
 * @param {string}  opts.key             Stable id (slug — alphanumeric/dash/underscore;
 *                                       not validated, callers responsible).
 * @param {string}  opts.label           Primary label text (HTML-escaped by helper).
 * @param {boolean} opts.expanded        Initial expanded state.
 * @param {string}  [opts.secondaryHtml] Raw HTML right of the chevron (count text, status dots, etc.).
 * @param {string}  [opts.actionHtml]    Raw HTML at the far right of the header (after the
 *                                       spacer) — e.g. a hover-revealed "See all" link. Clicks
 *                                       inside this region do NOT toggle the row — wrap in
 *                                       `.cs-section__action`.
 * @param {string}  opts.bodyHtml        Raw HTML body, shown when expanded.
 * @returns {string} HTML string.
 */
export function renderCollapsibleSection(opts) {
  const { key, label, expanded, secondaryHtml = '', actionHtml = '', bodyHtml = '' } = opts;
  const expandedClass = expanded ? ' cs-section--expanded' : '';
  return `
    <section class="cs-section${expandedClass}" data-cs-section="${key}">
      <button class="cs-section__header" type="button"
              data-cs-toggle data-cs-key="${key}" aria-expanded="${!!expanded}">
        <span class="cs-section__label">${escapeHtml(label)}</span>
        <svg class="cs-section__chevron" width="14" height="14" viewBox="-1 0 20 20" fill="none" aria-hidden="true">
          <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        ${secondaryHtml ? `<span class="cs-section__secondary">${secondaryHtml}</span>` : ''}
        <span class="cs-section__spacer"></span>
        ${actionHtml}
      </button>
      <div class="cs-section__body">${bodyHtml}</div>
    </section>
  `;
}

/**
 * Wire a single delegated click handler on `root` for collapsible-section toggles.
 * Clicks inside `.cs-section__action` are ignored (the action handles its own behavior).
 *
 * @param {HTMLElement} root
 * @param {Object}      handlers
 * @param {(key: string) => void} handlers.onToggle  Caller updates its own state + DOM.
 * @returns {void}
 */
export function attachCollapsibleSectionHandlers(root, { onToggle }) {
  if (!root || typeof onToggle !== 'function') return;
  root.addEventListener('click', (e) => {
    if (e.target.closest('.cs-section__action')) return;
    const toggle = e.target.closest('[data-cs-toggle]');
    if (!toggle || !root.contains(toggle)) return;
    const key = toggle.dataset.csKey;
    if (key) onToggle(key);
  });
}

/**
 * Imperatively set a section's DOM state. Use from outside the click flow
 * (e.g. when a scroll-to-section link wants to expand a collapsed target).
 * No-op if root is null or the section element is not found.
 *
 * @param {HTMLElement|null} root      Element containing the section.
 * @param {string}           key       Section key.
 * @param {boolean}          expanded  Desired state.
 * @returns {void}
 */
export function setCollapsibleSectionExpanded(root, key, expanded) {
  if (!root) return;
  const sectionEl = root.querySelector(`.cs-section[data-cs-section="${key}"]`);
  if (!sectionEl) return;
  sectionEl.classList.toggle('cs-section--expanded', !!expanded);
  const btn = sectionEl.querySelector('.cs-section__header');
  if (btn) btn.setAttribute('aria-expanded', String(!!expanded));
}

/**
 * Per-user localStorage key convention for a collapsible section's expand
 * state — namespaced by userId so collapse state doesn't bleed across
 * accounts sharing a browser profile (relevant given the couple/partner
 * feature: plausible household device sharing).
 *
 * No single existing localStorage-key convention was worth extending
 * verbatim here (orientation-* uses hyphens; upgrade_service_state is a
 * flat, non-interpolated key) — this is a reasonable new pattern, not an
 * extension of an existing one.
 */
const STORAGE_PREFIX = 'sidebar_recents';

/**
 * Read a section's persisted expand state for a given user.
 * @param {string} key - Section key (e.g. 'recents')
 * @param {string} userId - Current user's id (namespaces the storage key)
 * @param {boolean} [defaultExpanded=true] - Fallback when nothing is stored yet
 * @returns {boolean}
 */
export function loadCollapsedState(key, userId, defaultExpanded = true) {
  const stored = localStorage.getItem(`${STORAGE_PREFIX}_${userId}_${key}_expanded`);
  return stored === null ? defaultExpanded : stored === '1';
}

/**
 * Persist a section's expand state for a given user.
 * @param {string} key - Section key (e.g. 'recents')
 * @param {string} userId - Current user's id (namespaces the storage key)
 * @param {boolean} expanded - Current expand state
 * @returns {void}
 */
export function saveCollapsedState(key, userId, expanded) {
  localStorage.setItem(`${STORAGE_PREFIX}_${userId}_${key}_expanded`, expanded ? '1' : '0');
}
